import { supabase } from "@/integrations/supabase/client";

const SUPABASE_URL = "https://siypiyiaqxqyzztwpcao.supabase.co";
const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNpeXBpeWlhcXhxeXp6dHdwY2FvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzA4NjI0MjYsImV4cCI6MjA4NjQzODQyNn0.SY3vD38jOG6_sZKC4JzAAQ13aXju2I7Ldk-EyOZxvSY";

/** Minimum remaining lifetime (seconds) before we proactively refresh the token */
const TOKEN_REFRESH_MARGIN_S = 60;

function decodeTokenExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Coalesces concurrent refreshSession() calls into a single in-flight promise.
 * Without this, multiple parallel apiFetch() calls can each trigger their own
 * refresh and fight over the Supabase auth lock, producing
 * "Lock ... was released because another request stole it" errors.
 */
let refreshInFlight: Promise<import("@supabase/supabase-js").Session | null> | null = null;

function refreshSessionCoalesced() {
  if (!refreshInFlight) {
    refreshInFlight = supabase.auth.refreshSession()
      .then(({ data }) => data.session ?? null)
      .catch(() => null)
      .finally(() => {
        // Clear after the microtask so concurrent awaiters all see the same result
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  let { data: { session } } = await supabase.auth.getSession();

  if (session?.access_token) {
    const exp = decodeTokenExp(session.access_token);
    const nowSec = Math.floor(Date.now() / 1000);
    if (exp && exp - nowSec < TOKEN_REFRESH_MARGIN_S) {
      // Token is near-expiry or already expired — refresh (coalesced)
      session = await refreshSessionCoalesced();
    }
  }

  if (!session?.access_token) {
    // No valid session even after refresh attempt — force logout
    await supabase.auth.signOut();
    window.location.href = "/login";
    throw new Error("Sessão expirada. Faça login novamente.");
  }

  return {
    Authorization: `Bearer ${session.access_token}`,
    apikey: ANON_KEY,
  };
}

export async function apiFetch<T = any>(
  functionName: string,
  params?: Record<string, string>,
  options?: { method?: string; body?: any; formData?: FormData }
): Promise<T> {
  const headers = await getAuthHeaders();
  const url = new URL(`${SUPABASE_URL}/functions/v1/${functionName}`);
  
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  }

  const fetchOptions: RequestInit = {
    method: options?.method || "GET",
    headers: options?.formData
      ? { ...headers }
      : { ...headers, "Content-Type": "application/json" },
  };

  if (options?.formData) {
    fetchOptions.body = options.formData;
  } else if (options?.body) {
    fetchOptions.body = JSON.stringify(options.body);
  }

  const res = await fetch(url.toString(), fetchOptions);

  // Handle 401 — session is truly invalid, force logout
  if (res.status === 401) {
    await supabase.auth.signOut();
    window.location.href = "/login";
    throw new Error("Sessão expirada. Faça login novamente.");
  }

  let data: any;
  try {
    data = await res.json();
  } catch {
    // Response is not valid JSON (e.g. edge function crash)
    if (!res.ok) {
      throw new Error(`Erro ${res.status}: o servidor retornou uma resposta inválida`);
    }
    data = {};
  }

  if (!res.ok) {
    throw new Error(data.error || `Erro ${res.status}`);
  }

  return data;
}
